import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDiet, MealSelection } from "@/contexts/DietContext";
import { useAuth } from "@/contexts/AuthContext";
import { savePendingMeasures } from "@/lib/pendingMeasures";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { Check, ChevronDown, ChevronUp, Info, BookOpen, Loader2, Sparkles } from "lucide-react";
import RecipeEmoji from "@/components/RecipeEmoji";
import { mealOrder, type MealKey } from "@/data/recipes";
import { useSavedRecipes, getRecipeTotals } from "@/hooks/useSavedRecipes";
import { useDietRecipes } from "@/hooks/useDietRecipes";
import { generateDiet, getOptionsCount } from "@/lib/dietGenerator";
import { apiFetch } from "@/lib/api";
import { clearPendingDietPreferences, loadPendingDietPreferences, savePendingDietPreferences } from "@/lib/pendingDietCheckout";
import { toast } from "sonner";
import DietBuilderSkeleton from "@/components/diet/DietBuilderSkeleton";
import { useDragSelect } from "@/hooks/useDragSelect";
import { mergeUserRecipesIntoBlocks } from "@/lib/savedRecipeConverter";
import { calcMacroTargets } from "@/lib/macroSolver";

const DietBuilder = () => {
  const { saveDiet, measures, setMeasures, selectedPlan, planLoading, dietCredits, subscriptionExpiry, lockedSex, lockedHeight, refreshSubscription } = useDiet();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoGenerateRef = useRef(false);
  const { recipes: userRecipes, globalRecipes, loading: recipesLoading } = useSavedRecipes();
  const { enrichedRecipes: recipes, loading: nutritionLoading } = useDietRecipes();

  // Preferences: per-meal list of selected recipe IDs
  const [preferences, setPreferences] = useState<Record<MealKey, string[]>>(() => {
    const pending = loadPendingDietPreferences();
    if (pending) return pending;
    return {
      breakfast: [],
      morning_snack: [],
      lunch: [],
      snack: [],
      dinner: [],
      optional: [],
    };
  });

  const [expandedMeals, setExpandedMeals] = useState<Record<string, boolean>>({ breakfast: true, lunch: true });
  const [expandedRecipe, setExpandedRecipe] = useState<string | null>(null);
  const [showSavedSection, setShowSavedSection] = useState<Record<string, boolean>>({});
  const [generating, setGenerating] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(() => searchParams.get("auto") === "true");
  const [emptyMainMeals, setEmptyMainMeals] = useState<MealKey[]>([]);

  const allSavedRecipes = useMemo(() => [...userRecipes, ...globalRecipes], [userRecipes, globalRecipes]);

  const targetCalories = measures?.calories || 2000;
  const optionsCount = getOptionsCount(selectedPlan);

  const togglePreference = useCallback((mealKey: MealKey, foodId: string) => {
    setPreferences(prev => {
      const current = prev[mealKey];
      const isSelected = current.includes(foodId);
      return {
        ...prev,
        [mealKey]: isSelected ? current.filter(id => id !== foodId) : [...current, foodId],
      };
    });
    setEmptyMainMeals(prev => prev.filter(m => m !== mealKey));
  }, []);

  const isSelected = useCallback((mealKey: MealKey, recipeId: string) => {
    return preferences[mealKey]?.includes(recipeId) ?? false;
  }, [preferences]);

  const { onPointerDown, onPointerEnter, onPointerUp } = useDragSelect({
    onSelect: togglePreference,
    isSelected,
    disabled: generating,
  });

  // Global pointer up to end drag even if pointer leaves the grid
  useEffect(() => {
    window.addEventListener("pointerup", onPointerUp);
    return () => window.removeEventListener("pointerup", onPointerUp);
  }, [onPointerUp]);

  // Auto-generate after returning from payment
  useEffect(() => {
    if (autoGenerateRef.current) return;
    const isAuto = searchParams.get("auto") === "true";
    const hasPending = loadPendingDietPreferences();
    if (isAuto && hasPending && !planLoading && !recipesLoading && !nutritionLoading && measures && !needsPayment()) {
      autoGenerateRef.current = true;
      setAutoGenerating(true);
      setTimeout(() => handleGenerate(), 500);
    }
  }, [searchParams, planLoading, recipesLoading, nutritionLoading, selectedPlan, dietCredits, measures]);

  useEffect(() => {
    const paymentSuccess = searchParams.get("payment") === "success";
    const hasPending = loadPendingDietPreferences();

    if (!paymentSuccess || !hasPending || autoGenerateRef.current) return;

    let cancelled = false;
    setAutoGenerating(true);

    const syncSubscriptionAfterPayment = async () => {
      const loadingId = toast.loading("Pagamento confirmado! Ativando seu plano para gerar a dieta...");

      try {
        const MAX_ATTEMPTS = 15;
        let activated = false;

        for (let i = 0; i < MAX_ATTEMPTS && !cancelled; i++) {
          try {
            const res = await apiFetch<{ plan?: string; diet_credits?: number }>("api-subscription");
            const isActive = (res?.plan && res.plan !== "free") || (res?.diet_credits ?? 0) > 0;
            if (isActive) {
              activated = true;
              break;
            }
          } catch {}

          await new Promise((r) => setTimeout(r, 2000));
        }

        if (cancelled) return;

        await refreshSubscription();
        toast.dismiss(loadingId);

        if (!activated) {
          setAutoGenerating(false);
          toast.error("O pagamento ainda não foi confirmado. Aguarde alguns instantes e tente novamente.");
          navigate("/plans", { replace: true });
          return;
        }

        toast.success("Plano ativado com sucesso!");
      } catch {
        if (cancelled) return;
        toast.dismiss(loadingId);
        setAutoGenerating(false);
        toast.error("Não foi possível validar seu pagamento agora. Tente novamente em instantes.");
        navigate("/plans", { replace: true });
      }
    };

    syncSubscriptionAfterPayment();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, refreshSubscription]);

  const totalSelected = useMemo(() => {
    return Object.values(preferences).reduce((sum, ids) => sum + ids.length, 0);
  }, [preferences]);

  const toggleMealExpand = (key: string) => {
    setExpandedMeals(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const needsPayment = () => {
    // Credits allow generation regardless of plan label (defensive against out-of-sync subscription rows)
    if (dietCredits > 0) return false;
    if (!selectedPlan || selectedPlan === "free") return true;
    if (selectedPlan === "basic") return true; // basic without credits
    if ((selectedPlan === "plus" || selectedPlan === "complete") && subscriptionExpiry) {
      if (new Date(subscriptionExpiry) < new Date()) return true;
    }
    return false;
  };

  const handleGenerate = async () => {
    // Validate main meals have at least 3 selections (run first so anonymous users get same feedback)
    const mainMeals: MealKey[] = ["breakfast", "lunch", "dinner"];
    const missingMeals = mainMeals.filter(m => preferences[m].length < 3);
    if (missingMeals.length > 0) {
      setEmptyMainMeals(missingMeals);
      const mealLabels: Record<string, string> = {
        breakfast: "Café da Manhã",
        lunch: "Almoço",
        dinner: "Jantar",
      };
      const names = missingMeals.map(m => mealLabels[m]).join(", ");
      toast.error(`Selecione pelo menos 3 alimentos em: ${names}`);
      return;
    }
    setEmptyMainMeals([]);

    // Not logged in: persist measures + preferences and force registration before paying
    if (!user) {
      savePendingDietPreferences(preferences);
      if (measures) savePendingMeasures(measures);
      toast.info("Crie sua conta para gerar a dieta.");
      navigate("/register?next=/plans");
      return;
    }

    if (needsPayment()) {
      // Save preferences so they survive the payment redirect
      savePendingDietPreferences(preferences);
      toast.info("Escolha um plano para gerar sua dieta automaticamente.");
      navigate("/plans");
      return;
    }


    setGenerating(true);
    try {
      const derivedMacroTargets = measures?.weight && measures?.goal && targetCalories
        ? calcMacroTargets(measures.weight, measures.goal, targetCalories)
        : undefined;
      const macroTargetsForGeneration = derivedMacroTargets ?? measures?.macroTargets;

      // Save meal preferences to measures so MyDiet can use them for substitutions
      if (measures) {
        const updatedMeasures = {
          ...measures,
          mealPreferences: preferences,
          macroTargets: macroTargetsForGeneration,
        };
        setMeasures(updatedMeasures);
      }

      // Yield to browser to show loading state before heavy computation
      await new Promise(r => setTimeout(r, 100));

      // Merge user-created recipes into recipe blocks so the generator can find & scale them
      const mergedRecipes = mergeUserRecipesIntoBlocks(recipes, preferences, allSavedRecipes);

      // Run heavy diet generation in a yielding wrapper to prevent browser freeze
      const generatedMeals = await new Promise<ReturnType<typeof generateDiet>>((resolve) => {
        setTimeout(() => {
          resolve(generateDiet({
            preferences,
            targetCalories,
            macroTargets: macroTargetsForGeneration
              ? { ...macroTargetsForGeneration, cal: targetCalories }
              : undefined,
            optionsCount,
            recipes: mergedRecipes,
          }));
        }, 0);
      });

      await saveDiet(undefined, generatedMeals);
      clearPendingDietPreferences();
      toast.success(`Dieta gerada com ${optionsCount} opções por refeição!`);
      navigate("/my-diet", { replace: true });
    } catch (e: any) {
      console.error("Diet generation failed:", e);
      const msg = e?.message || "";
      if (msg.includes("créditos") || msg.includes("expirou") || msg.includes("perfil")) {
        toast.error(msg);
      } else {
        toast.error("Erro ao gerar dieta. Tente novamente.");
      }
    } finally {
      setGenerating(false);
    }
  };

  if (planLoading || recipesLoading || nutritionLoading) {
    return <DietBuilderSkeleton />;
  }

  if (!measures) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-muted-foreground mb-4">Preencha suas medidas primeiro</p>
        <Button onClick={() => navigate("/dashboard")} className="gradient-primary text-primary-foreground">
          Ir para medidas
        </Button>
      </div>
    );
  }

  if (autoGenerating) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-6">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col items-center gap-5"
        >
          <div className="relative">
            <Loader2 className="w-12 h-12 text-primary animate-spin" />
            <Sparkles className="w-5 h-5 text-primary absolute -top-1 -right-1 animate-pulse" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Pagamento confirmado! 🎉</h2>
          <p className="text-muted-foreground max-w-sm">
            Estamos gerando sua dieta personalizada com os alimentos que você escolheu. Aguarde alguns segundos...
          </p>
          <div className="w-48 h-2 rounded-full bg-muted overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-primary"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 8, ease: "linear" }}
            />
          </div>
        </motion.div>
      </div>
    );
  }


  const renderRecipeCard = (recipe: typeof recipes[MealKey]["items"][0], mealKey: MealKey, selected: boolean, showIngredients: boolean) => (
    <div key={recipe.id} className="relative">
      <div
        onPointerDown={(e) => {
          e.preventDefault();
          onPointerDown(mealKey, recipe.id, e);
        }}
        onPointerEnter={() => onPointerEnter(mealKey, recipe.id)}
        onClick={(e) => {
          // For touch devices: drag-select skips onPointerDown, so handle tap here
          if (window.matchMedia("(pointer: coarse)").matches) {
            togglePreference(mealKey, recipe.id);
          }
        }}
        className={`w-full h-full min-h-[90px] flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${
          selected
            ? "border-primary bg-primary text-primary-foreground shadow-md"
            : "border-border hover:border-primary/30"
        } ${generating ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {selected && (
          <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full gradient-primary flex items-center justify-center">
            <Check className="w-2.5 h-2.5 text-primary-foreground" />
          </div>
        )}
        <RecipeEmoji emoji={recipe.emoji} />
        <span className={`text-xs font-medium text-center line-clamp-2 leading-tight ${selected ? "text-primary-foreground" : "text-foreground"}`}>{recipe.label}</span>
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          setExpandedRecipe(showIngredients ? null : recipe.id);
        }}
        className="absolute top-1 left-1 w-4 h-4 rounded-full bg-muted flex items-center justify-center hover:bg-muted-foreground/20 transition-colors"
      >
        <Info className="w-2.5 h-2.5 text-muted-foreground" />
      </button>

      {showIngredients && (
        <Dialog open={showIngredients} onOpenChange={() => setExpandedRecipe(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <RecipeEmoji emoji={recipe.emoji} /> {recipe.label}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-1.5">
              {recipe.ingredients.map((ing, i) => (
                <div key={i} className="flex items-center justify-between text-sm gap-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-foreground">{ing.name}</span>
                    {ing.source && (
                      <span className={`shrink-0 text-[9px] leading-tight px-1.5 py-0.5 rounded font-bold uppercase ${
                        ing.source === 'TACO' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400' :
                        ing.source === 'TBCA' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400' :
                        ing.source === 'ESTIMADO' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400' :
                        'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400'
                      }`}>
                        {ing.source}
                      </span>
                    )}
                    <span className="text-muted-foreground shrink-0">({ing.amount})</span>
                  </div>
                  <div className="flex gap-2 text-muted-foreground whitespace-nowrap ml-2">
                    <span>{ing.cal}cal</span>
                    <span>{ing.protein}g P</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-1 pt-2 border-t border-border flex justify-between text-sm font-semibold">
              <span className="text-foreground">Total</span>
              <div className="flex gap-2">
                <span className="text-primary">{recipe.cal} kcal</span>
                <span className="text-foreground">{recipe.protein}g P</span>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Montar Dieta</h1>
        <p className="text-muted-foreground">Meta: <span className="text-primary font-semibold">{targetCalories} kcal</span> • {optionsCount} opções por refeição</p>
      </div>

      {/* Explainer */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-foreground font-medium">Selecione os alimentos que você gosta</p>
            <p className="text-xs text-muted-foreground mt-1">
              Marque os itens que gosta em cada refeição. O sistema vai montar sua dieta automaticamente com {optionsCount} opções por refeição, encaixando tudo no seu alvo calórico.
            </p>
          </div>
        </div>
      </div>

      {/* Meal Blocks */}
      {mealOrder.map(key => {
        const block = recipes[key];
        const isExpanded = expandedMeals[key] !== false;
        const showSaved = showSavedSection[key] || false;
        const selectedCount = preferences[key].length;

        return (
          <div key={key} className={`bg-card rounded-2xl shadow-card border overflow-visible ${
            emptyMainMeals.includes(key) ? "border-destructive border-2" : "border-border"
          }`}>
            <button
              onClick={() => toggleMealExpand(key)}
              className="w-full flex items-center justify-between p-5"
            >
              <div className="flex items-center gap-2">
                <RecipeEmoji emoji={block.emoji} />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-foreground">{block.label}</h2>
                  <p className="text-xs text-muted-foreground">
                    {selectedCount > 0 ? (
                      <span className="text-primary font-medium">{selectedCount} selecionado(s)</span>
                    ) : (
                      "Nenhum selecionado"
                    )}
                  </p>
                </div>
              </div>
              {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
            </button>

            {isExpanded && (
              <div className="px-5 pb-5 space-y-4 overflow-visible">
                <p className="text-xs text-muted-foreground italic bg-muted/50 rounded-lg px-3 py-2">
                  Selecione os alimentos que você gosta para o seu <strong>{block.label.toLowerCase()}</strong>.
                  <span className="hidden lg:block mt-1 text-primary/70">💡 Dica: no computador, clique e arraste para selecionar vários de uma vez!</span>
                </p>

                {/* Static recipes grid - grouped by category */}
                {(() => {
                  const isStructuredMeal = key === "lunch" || key === "dinner";
                  const structuredCategories: { key: string; label: string }[] = [
                    { key: "protein", label: "🥩 Proteínas" },
                    { key: "carb", label: "🍚 Carboidratos" },
                    { key: "legume", label: "🫘 Leguminosas" },
                    { key: "salad", label: "🥗 Saladas e Verduras" },
                    { key: "prep", label: "🥪 Preparações" },
                  ];

                  // Display groups for breakfast / snack / morning_snack
                  const FRUIT_IDS = new Set([
                    "melancia_cafe", "mamao_cafe", "melao_cafe", "morango_cafe", "banana_cafe", "maca_cafe", "laranja_cafe",
                    "melancia_tarde", "mamao_tarde", "melao_tarde", "morango_tarde", "banana_tarde", "maca_tarde", "laranja_tarde",
                    "banana_lm", "maca_lm", "melao_lm", "mamao_lm", "melancia_lm", "abacaxi_lm", "uva_lm", "morango_lm",
                    "laranja_lm", "pera_lm", "kiwi_lm", "caqui_lm", "bergamota_lm",
                  ]);
                  const BEVERAGE_IDS = new Set([
                    "cafe_puro", "cafe_leite_desnatado", "leite_desnatado_cafe", "suco_verde_cafe",
                    "cafe_puro_tarde", "cafe_leite_desnatado_tarde", "leite_desnatado_tarde", "suco_verde_tarde",
                    "leite_desnatado_lm",
                  ]);
                  const ADDITIONAL_IDS = new Set([
                    "pasta_amendoim_avulsa", "granola_cafe", "whey_cafe", "biscoito_magic_toast",
                    "iogurte_desnatado", "iogurte_zero", "iogurte_proteico",
                    "pasta_amendoim_tarde", "granola_tarde", "whey_tarde", "biscoito_magic_toast_tarde",
                    "iogurte_desnatado_tarde", "iogurte_zero_tarde", "iogurte_proteico_tarde",
                    "biscoito_arroz_lm", "pasta_amendoim_lm", "ovo_lm",
                    "iogurte_natural_lm", "iogurte_zero_lm", "iogurte_proteico_lm",
                    "biscoito_magic_toast_lm", "whey_lm",
                  ]);

                  const simpleCategories: { key: string; label: string }[] = [
                    { key: "prep", label: "🍳 Preparações" },
                    { key: "fruit", label: "🍎 Frutas" },
                    { key: "beverage", label: "☕ Bebidas" },
                    { key: "additional", label: "🥄 Adicionais" },
                  ];

                  const getSimpleGroup = (id: string): string => {
                    if (FRUIT_IDS.has(id)) return "fruit";
                    if (BEVERAGE_IDS.has(id)) return "beverage";
                    if (ADDITIONAL_IDS.has(id)) return "additional";
                    return "prep";
                  };

                  const isGroupedSimpleMeal = key === "breakfast" || key === "snack" || key === "morning_snack";

                  if (!isStructuredMeal && !isGroupedSimpleMeal) {
                    // Flat grid for optional/other
                    return (
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-2 overflow-visible select-none">
                        {block.items.map(recipe => {
                          const selected = preferences[key].includes(recipe.id);
                          const showIngredients = expandedRecipe === recipe.id;
                          return renderRecipeCard(recipe, key, selected, showIngredients);
                        })}
                      </div>
                    );
                  }

                  const categoryOrder = isStructuredMeal ? structuredCategories : simpleCategories;

                  // Group items
                  const grouped = new Map<string, typeof block.items>();
                  for (const recipe of block.items) {
                    const cat = isStructuredMeal
                      ? (recipe.category || "other")
                      : getSimpleGroup(recipe.id);
                    if (!grouped.has(cat)) grouped.set(cat, []);
                    grouped.get(cat)!.push(recipe);
                  }

                  return (
                    <div className="space-y-3">
                      {categoryOrder.map(({ key: catKey, label: catLabel }) => {
                        const items = grouped.get(catKey);
                        if (!items || items.length === 0) return null;
                        return (
                          <div key={catKey}>
                            <p className="text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">{catLabel}</p>
                            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 overflow-visible select-none">
                              {items.map(recipe => {
                                const selected = preferences[key].includes(recipe.id);
                                const showIngredients = expandedRecipe === recipe.id;
                                return renderRecipeCard(recipe, key, selected, showIngredients);
                              })}
                            </div>
                          </div>
                        );
                      })}
                      {/* Items without a recognized category (structured meals only) */}
                      {isStructuredMeal && block.items.filter(r => !r.category || !categoryOrder.some(c => c.key === r.category)).length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">📋 Outros</p>
                          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 overflow-visible select-none">
                            {block.items.filter(r => !r.category || !categoryOrder.some(c => c.key === r.category)).map(recipe => {
                              const selected = preferences[key].includes(recipe.id);
                              const showIngredients = expandedRecipe === recipe.id;
                              return renderRecipeCard(recipe, key, selected, showIngredients);
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Saved recipes toggle */}
                {allSavedRecipes.length > 0 && (
                  <div>
                    <button
                      onClick={() => setShowSavedSection(prev => ({ ...prev, [key]: !prev[key] }))}
                      className="flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                    >
                      <BookOpen className="w-4 h-4" />
                      {showSaved ? "Ocultar" : "Mostrar"} receitas salvas ({allSavedRecipes.length})
                    </button>

                    {showSaved && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-3"
                      >
                        {allSavedRecipes.map(saved => {
                          const savedId = `saved_${saved.id}`;
                          const selected = preferences[key].includes(savedId);

                          return (
                            <button
                              key={saved.id}
                              onClick={() => togglePreference(key, savedId)}
                              disabled={generating}
                              className={`w-full flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all relative overflow-hidden ${
                                selected
                                  ? "border-primary bg-primary/5 shadow-sm"
                                  : "border-border hover:border-primary/30"
                              } ${generating ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              {saved.banner_image_url && (
                                <div className="absolute inset-0 z-0">
                                  <img src={saved.banner_image_url} alt="" className="w-full h-full object-cover opacity-15" />
                                </div>
                              )}
                              {selected && (
                                <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full gradient-primary flex items-center justify-center z-10">
                                  <Check className="w-2.5 h-2.5 text-primary-foreground" />
                                </div>
                              )}
                              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center overflow-hidden z-10">
                                {saved.banner_image_url ? (
                                  <img src={saved.banner_image_url} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <BookOpen className="w-3.5 h-3.5 text-primary" />
                                )}
                              </div>
                              <span className="text-xs font-medium text-foreground text-center truncate w-full relative z-10">{saved.name}</span>
                              {saved.is_global && (
                                <span className="text-[9px] text-muted-foreground bg-muted px-1 py-0.5 rounded relative z-10">Global</span>
                              )}
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Generate Button */}
      <div className="sticky bottom-4 z-40 pt-4">
        <Button
          onClick={handleGenerate}
          className="w-full gradient-primary text-primary-foreground hover:opacity-90 transition-opacity shadow-elevated"
          size="lg"
          disabled={totalSelected === 0 || generating}
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Gerando sua dieta...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Gerar Minha Dieta ({optionsCount} opções)
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
};

export default DietBuilder;
